import queryString from 'query-string';
import {
  IRegistrationsApiCall,
  IRestApiService,
  RegistrationData,
  RegistrationFilter,
  ClassSlot,
  ClientRegistrationInfo,
  BulkClassActionJobState,
  StartBulkClassActionPayload,
} from '../types';
import moment from 'moment';
import { RegistrationTracker } from '../services/analytics/apiTrackers';

class RegistrationsApiCall implements IRegistrationsApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getLastClassInfos = (ownerId: string, params: any): Promise<any> => {
    const url = `registrations/${ownerId}/last-class-infos`;
    return this.restApiService.post(url, params);
  };

  getInvoice = (ownerId: string, from: Date, to: Date): Promise<any> => {
    const url = `registrations/${ownerId}/get-invoice?from=${from.toISOString()}&to=${to.toISOString()}`;
    return this.restApiService.post(url);
  };

  getUpcomingEvents = (ownerId: string, studioId: string, bookFrom: string, timezone: string): Promise<any> => {
    const url = `registrations/${ownerId}/${studioId}/upcoming-events?bookFrom=${bookFrom}&timezone=${timezone}`;
    return this.restApiService.get(url);
  };

  validateStudioOnHold = (ownerId: string, studioId: string, from: string, to: string): Promise<any> => {
    const url = `registrations/${ownerId}/${studioId}/validate-studio-on-hold`;
    return this.restApiService.post(url, { from: from, to: to });
  };

  findFirstUnpaidClass(
    ownerId: string,
    studioClientId: string,
    billingTags: string[],
    studioIds: string[]
  ): Promise<any> {
    const url = `registrations/${ownerId}/${studioClientId}/unpaid-class`;
    return this.restApiService.post(url, { billingTags, studioIds });
  }

  isRoomAvailable = (
    ownerId: string,
    studioId: string,
    roomIds: string[],
    classDateTime: string,
    duration: number,
    excludeClass: string
  ): Promise<any> => {
    const url = `registrations/${ownerId}/${studioId}/validate-room`;
    return this.restApiService.post(url, {
      excludeClass: excludeClass,
      classDateTime: classDateTime,
      duration: duration,
      roomIds: roomIds,
    });
  };

  getClientsAllRegistrations(from: any, to: any, ownerId?: string): Promise<any> {
    let url = `registrations/my-all?from=${from.format('DD-MM-YYYY')}&to=${to.format('DD-MM-YYYY')}`;
    if (ownerId) {
      url += `&ownerId=${ownerId}`;
    }
    return this.restApiService.get(url);
  }

  getAttendanceOnRatePlan(ownerId: string, clientId: string, ratePlanId: string): Promise<any> {
    const url = `registrations/${ownerId}/${clientId}/by-rate-plan/${ratePlanId}`;
    return this.restApiService.get(url);
  }

  getVisitsDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getTeacherDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/teachers?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-monthly?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getEnhancedMonthlyStatistics(ownerId: string, from: any, to: any): Promise<any> {
    const url = `registrations/${ownerId}/dashboard/monthly-statistics?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;
    return this.restApiService.get(url);
  }

  getVisitsWithLowRetentionMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/low-retention-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsWithNoRetentionMonthlyDashboard(
    ownerId: string,
    from: any,
    to: any,
    numberOfAttendance: number,
    filter: any
  ): Promise<any> {
    const queryParams = new URLSearchParams({
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
      numberOfAttendance: numberOfAttendance.toString(),
    });

    const url = `registrations/${ownerId}/dashboard/no-retention-monthly?${queryParams.toString()}`;

    const filterData = {
      studioId: filter.studioId || '',
      billingTag: filter.billingTag || '',
      tag: filter.tag || '',
      includeClientsWithoutAttendance:
        filter.includeClientsWithoutAttendance !== undefined ? filter.includeClientsWithoutAttendance : true,
      ...(typeof filter.hasPendingClient === 'boolean' ? { hasPendingClient: filter.hasPendingClient } : {}),
    };

    return this.restApiService.post(url, filterData);
  }

  // Background version of getVisitsWithNoRetentionMonthlyDashboard above ("Мої клієнти" ->
  // "Низька відвідуваність") - same query-string (from/to/numberOfAttendance) and body filter
  // shape, so the export doesn't force the (account-wide, unbounded) aggregation onto the request
  // thread. ownerId is only used to build the URL - the server always scopes the export to
  // req.user._id, same as the sync endpoint above.
  startLowRetentionExport(
    ownerId: string,
    from: any,
    to: any,
    numberOfAttendance: number,
    filter: any
  ): Promise<{ jobId: string; downloadUrl: string }> {
    const queryParams = new URLSearchParams({
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
      numberOfAttendance: numberOfAttendance.toString(),
    });

    const url = `registrations/${ownerId}/dashboard/no-retention-monthly/export-async?${queryParams.toString()}`;

    const filterData = {
      studioId: filter.studioId || '',
      billingTag: filter.billingTag || '',
      tag: filter.tag || '',
      includeClientsWithoutAttendance: !!filter.includeClientsWithoutAttendance,
      ...(typeof filter.hasPendingClient === 'boolean' ? { hasPendingClient: filter.hasPendingClient } : {}),
    };

    return this.restApiService.post(url, filterData);
  }

  getVisitsByBillingTagDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-billing-tag?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsByBillingTagMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-billing-tag-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsByRetentionMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-retention-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsByClientTagMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-client-tag-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsByUniqueTagMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-unique-client-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  getVisitsByPaymentTypeMonthlyDashboard(
    studioId: string,
    from: any,
    to: any,
    teacherIds?: string[],
    billingTags?: string[],
    clientTags?: string[]
  ): Promise<any> {
    let url = `registrations/${studioId}/dashboard/visits-by-payment-type?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    if (teacherIds) {
      url += `&${queryString.stringify({ teacherIds })}`;
    }
    if (billingTags) {
      url += `&${queryString.stringify({ billingTags })}`;
    }
    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }
    return this.restApiService.get(url);
  }

  cancelMyRegistration(registrationId: string): Promise<any> {
    const url = 'registrations/my/' + registrationId;
    return this.restApiService.delete(url);
  }

  cancelMyRegistrationByReason(data: {
    ownerId: string;
    studioId: string;
    groupId: string;
    cancellationReason?: string;
    clientId: string;
    isCancelingWithForfeit: boolean;
    clientRatePlanId: string;
  }): Promise<any> {
    const {
      ownerId,
      studioId,
      groupId,
      cancellationReason = '',
      clientId,
      isCancelingWithForfeit,
      clientRatePlanId,
    } = data;
    const url = `registrations/my/${groupId}/cancel`;

    return this.restApiService.post(url, {
      ownerId: ownerId,
      studioId: studioId,
      cancellationReason: cancellationReason,
      clientId: clientId,
      isCancelingWithForfeit: isCancelingWithForfeit,
      clientRatePlanId: clientRatePlanId,
    });
  }

  moveMyClasses(ownerId: string, studioId: string, details: any): Promise<any> {
    const url = `registrations/${ownerId}/${studioId}/move-classes`;
    return this.restApiService.post(url, details);
  }

  registerMeToClass(registration: any, studioId: string, groupId: string): Promise<any> {
    return this.restApiService.post('registrations/' + groupId, {
      studioId: studioId,
      registration: registration,
    });
  }

  getRegistrationsReport(studioId: string, filter: RegistrationFilter): Promise<any> {
    let url = 'registrations/' + studioId + '/report?';

    if (filter.from) {
      url += 'from=' + moment.utc(filter.from).format('DD-MM-YYYY') + '&';
    }
    if (filter.to) {
      url += 'to=' + moment.utc(filter.to).format('DD-MM-YYYY') + '&';
    }
    if (filter.teacherIds && filter.teacherIds.length > 0) {
      url += `${queryString.stringify({ teacherIds: filter.teacherIds })}&`;
    }
    if (filter.courseIds && filter.courseIds.length > 0) {
      url += `${queryString.stringify({ courseIds: filter.courseIds })}&`;
    }
    if (filter.clientTags && filter.clientTags.length > 0) {
      url += `${queryString.stringify({ clientTags: filter.clientTags })}&`;
    }
    if (filter.billingTags && filter.billingTags.length > 0) {
      url += `${queryString.stringify({ billingTags: filter.billingTags })}&`;
    }
    if (filter.hideCanceledClasses) {
      url += `hideCanceledClasses=${filter.hideCanceledClasses}&`;
    }
    if (filter.showCanceledClasses) {
      url += `showCanceledClasses=${filter.showCanceledClasses}&`;
    }
    if (filter.showActiveClasses) {
      url += `showActiveClasses=${filter.showActiveClasses}&`;
    }

    return this.restApiService.get(url);
  }

  getSchoolReport(studioId: string, filter: RegistrationFilter, page?: number, limit?: number): Promise<any> {
    let url = 'registrations/' + studioId + '/school-report?';
    url += this.buildSchoolReportQuery(filter);
    // Matches the server's own bounds check (page/limit are 1-based); a plain
    // truthiness check would also (mis)treat negative/NaN values as valid.
    if ((page as number) > 0 && (limit as number) > 0) {
      url += `page=${page}&limit=${limit}&`;
    }
    return this.restApiService.get(url);
  }

  getSchoolReportExport(studioId: string, filter: RegistrationFilter): Promise<any> {
    let url = 'registrations/' + studioId + '/school-report/export?';
    url += this.buildSchoolReportQuery(filter);
    return this.restApiService.get(url);
  }

  // Background version of getSchoolReportExport - same query-string filter shape (the server's
  // attendanceValidator reads from req.query, same as school-report/export), plus the client-side
  // filters (roomIds/showDebtClasses/searchText) that ScheduleListView.tsx's filterRows applies
  // after fetching, sent in the body since the server now replicates that same filtering itself.
  startScheduleListExport(
    studioId: string,
    filter: RegistrationFilter,
    clientSideFilter: { roomIds?: string[]; showDebtClasses?: boolean; searchText?: string }
  ): Promise<{ jobId: string; downloadUrl: string }> {
    let url = 'registrations/' + studioId + '/school-report/export-async?';
    url += this.buildSchoolReportQuery(filter);
    return this.restApiService.post(url, clientSideFilter);
  }

  // Background version of the SchoolView.tsx pivot ("Шкільний журнал"). Same base query-string
  // filter as getSchoolReport/school-report; selectedGroupTag (the pivot's client/column scope,
  // and the exported client-name column's header) isn't part of RegistrationFilter, so it goes in
  // the body, same pattern as startScheduleListExport's client-side-only filters.
  startSchoolJournalExport(
    studioId: string,
    filter: RegistrationFilter,
    selectedGroupTag: string | undefined
  ): Promise<{ jobId: string; downloadUrl: string }> {
    let url = 'registrations/' + studioId + '/school-journal/export-async?';
    url += this.buildSchoolReportQuery(filter);
    return this.restApiService.post(url, { selectedGroupTag });
  }

  // Background version of ScheduleListView.tsx's "фільтри → Експорт" (exportTable/
  // exportDailyRoomsAnalyticsToExcel) - produces the same Excel pivot, now built server-side. Same
  // base query-string filter as getSchoolReportExport/getSchoolAggs; roomIds here is the pivot's
  // own output-level room filter (which room columns appear in the sheet, not a query filter -
  // see daily-rooms-analytics-export.adapter.ts), sent in the body like the other async exports.
  startDailyRoomsAnalyticsExport(
    studioId: string,
    filter: RegistrationFilter,
    roomIds: string[]
  ): Promise<{ jobId: string; downloadUrl: string }> {
    let url = 'registrations/' + studioId + '/daily-rooms-analytics/export-async?';
    url += this.buildSchoolReportQuery(filter);
    return this.restApiService.post(url, { roomIds });
  }

  // Bulk cancel/confirm (VOOP-2454) - see ui/runBulkClassAction.ts, the only caller that drives the
  // POST-then-maybe-poll timing against this pair.
  startBulkClassAction(studioId: string, payload: StartBulkClassActionPayload): Promise<BulkClassActionJobState> {
    return this.restApiService.post(`registrations/bulk-class-action/${studioId}`, payload);
  }

  getBulkClassActionStatus(studioId: string, jobId: string): Promise<BulkClassActionJobState> {
    return this.restApiService.get(`registrations/bulk-class-action/${studioId}/${jobId}/status`);
  }

  private buildSchoolReportQuery(filter: RegistrationFilter): string {
    let query = '';

    if (filter.from) {
      query += 'from=' + moment.utc(filter.from).format('DD-MM-YYYY') + '&';
    }
    if (filter.to) {
      query += 'to=' + moment.utc(filter.to).format('DD-MM-YYYY') + '&';
    }
    if (filter.teacherIds && filter.teacherIds.length > 0) {
      query += `${queryString.stringify({ teacherIds: filter.teacherIds })}&`;
    }
    if (filter.courseIds && filter.courseIds.length > 0) {
      query += `${queryString.stringify({ courseIds: filter.courseIds })}&`;
    }
    if (filter.clientTags && filter.clientTags.length > 0) {
      query += `${queryString.stringify({ clientTags: filter.clientTags })}&`;
    }
    if (filter.billingTags && filter.billingTags.length > 0) {
      query += `${queryString.stringify({ billingTags: filter.billingTags })}&`;
    }
    if (filter.roomIds && filter.roomIds.length > 0) {
      query += `${queryString.stringify({ roomIds: filter.roomIds })}&`;
    }
    if (filter.showActiveClasses) {
      query += `showActiveClasses=${filter.showActiveClasses}&`;
    }
    if (filter.showCanceledClasses) {
      query += `showCanceledClasses=${filter.showCanceledClasses}&`;
    }
    return query;
  }

  getSchoolAggs(studioId: string, filter: RegistrationFilter): Promise<any> {
    let url = 'registrations/' + studioId + '/school-aggs?';

    if (filter.from) {
      url += 'from=' + moment.utc(filter.from).format('DD-MM-YYYY') + '&';
    }
    if (filter.to) {
      url += 'to=' + moment.utc(filter.to).format('DD-MM-YYYY') + '&';
    }
    if (filter.teacherIds && filter.teacherIds.length > 0) {
      url += `${queryString.stringify({ teacherIds: filter.teacherIds })}&`;
    }
    if (filter.courseIds && filter.courseIds.length > 0) {
      url += `${queryString.stringify({ courseIds: filter.courseIds })}&`;
    }
    if (filter.clientTags && filter.clientTags.length > 0) {
      url += `${queryString.stringify({ clientTags: filter.clientTags })}&`;
    }
    if (filter.billingTags && filter.billingTags.length > 0) {
      url += `${queryString.stringify({ billingTags: filter.billingTags })}&`;
    }
    if (filter.roomIds && filter.roomIds.length > 0) {
      url += `${queryString.stringify({ roomIds: filter.roomIds })}&`;
    }
    if (filter.showActiveClasses) {
      url += `showActiveClasses=${filter.showActiveClasses}&`;
    }
    if (filter.showCanceledClasses) {
      url += `showCanceledClasses=${filter.showCanceledClasses}&`;
    }
    return this.restApiService.get(url);
  }

  getSchoolSalary(ownerId: string, filter: any): Promise<any> {
    const url = `registrations/${ownerId}/school-salary`;
    return this.restApiService.post(url, filter);
  }

  startSalaryRoomsExport(ownerId: string, filter: any): Promise<{ jobId: string; downloadUrl: string }> {
    return this.restApiService.post(`registrations/${ownerId}/school-salary/export-rooms-async`, filter);
  }

  startDailySalaryExport(ownerId: string, filter: any): Promise<{ jobId: string; downloadUrl: string }> {
    return this.restApiService.post(`registrations/${ownerId}/school-salary/export-daily-async`, filter);
  }

  startDailyTagSalaryExport(ownerId: string, filter: any): Promise<{ jobId: string; downloadUrl: string }> {
    return this.restApiService.post(`registrations/${ownerId}/school-salary/export-daily-tags-async`, filter);
  }

  getTeacherSalaryBySource(ownerId: string, teacherId: string, filter: any): Promise<any> {
    const url = `registrations/${ownerId}/school-salary/${teacherId}/by-source`;
    return this.restApiService.post(url, filter);
  }

  getCalendarReport(studioId: string, from: any, to: any, filter: RegistrationFilter): Promise<any> {
    let url = `registrations/${studioId}/calendar/report?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;

    if (filter.teacherId) {
      url += `&teacherId=${filter.teacherId}`;
    }
    if (filter.billingTag) {
      url += `&billingTag=${filter.billingTag}`;
    }
    if (filter.hideEmpty) {
      url += `&hideEmpty=${filter.hideEmpty}`;
    }
    if (filter.hideCanceledClassInfo) {
      url += `&hideCanceledClassInfo=${filter.hideCanceledClassInfo}`;
    }
    if (filter.showClientContact) {
      url += `&showClientContact=${filter.showClientContact}`;
    }
    if (filter.showClientRatePlans) {
      url += `&showClientRatePlans=${filter.showClientRatePlans}`;
    }
    if (filter.showClientBalance) {
      url += `&showClientBalance=${filter.showClientBalance}`;
    }
    if (filter.showLessonNumeration) {
      url += `&showLessonNumeration=${filter.showLessonNumeration}`;
    }
    if (filter.checkClientTags) {
      url += `&checkClientTags=${filter.checkClientTags}`;
    }
    if (filter.preview) {
      url += `&preview=true`;
    }

    const options: RequestInit | undefined = filter.signal
      ? {
          signal: filter.signal as AbortSignal,
        }
      : undefined;

    return this.restApiService.get(url, options);
  }

  getCalendarReportV2(studioId: string, from: any, to: any, filter: RegistrationFilter): Promise<any> {
    let url = `registrations/${studioId}/v2/calendar/report?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;

    if (filter.hideEmpty) {
      url += `&hideEmpty=${filter.hideEmpty}`;
    }

    return this.restApiService.get(url);
  }

  getCalendarClientReport(studioId: string, from: any, to: any, clientId: string): Promise<any> {
    const url = `registrations/${studioId}/calendar/client-report/${clientId}?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    return this.restApiService.get(url);
  }

  getRegistration(studioId: string, id: string, showClientBalance = false): Promise<any> {
    const query = showClientBalance ? '?showClientBalance=true' : '';
    return this.restApiService.get(`registrations/v2/${studioId}/${id}${query}`);
  }

  getClientRegistration(groupId: string, id: string): Promise<any> {
    return this.restApiService.get(`registrations/exact/${groupId}/${id}`);
  }

  getClassInfo(studioId: string, groupId: string): Promise<any> {
    return this.restApiService.get(`registrations/${studioId}/${groupId}`);
  }

  updateClassInfo(groupId: string, data: any): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .put(`registrations/${groupId}`, data)
      .then((result) => {
        RegistrationTracker.trackUpdateClassInfo({
          groupId,
          updateData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        RegistrationTracker.trackUpdateClassInfo({
          groupId,
          updateData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addClientToRegistration(studioId: string, registration: RegistrationData, client: any): Promise<any> {
    return this.restApiService.post(`registrations/${studioId}/${registration.groupId}/client`, {
      client: client,
      manual: registration,
    });
  }

  estimateAttendance(studioId: string, attendance: RegistrationData, client: any): Promise<any> {
    return this.restApiService.post(`registrations/${studioId}/${attendance.groupId}/estimate-client`, {
      client: client,
      ownerId: attendance.ownerId,
    });
  }

  noShowV2(
    studioId: string,
    attendance: RegistrationData,
    client: any,
    cancellationReason: string,
    noShowType: string,
    amount: number,
    notifyClient: boolean,
    depositAmount: number,
    source: string,
    clientRatePlanId?: string
  ): Promise<any> {
    return this.restApiService.post(`registrations/${studioId}/${attendance.groupId}/no-show`, {
      client: client,
      notifyClient: notifyClient,
      cancellationReason: cancellationReason,
      noShowType: noShowType,
      amount: amount,
      depositAmount: depositAmount,
      source: source,
      clientRatePlanId,
    });
  }

  addClass(studioId: string, registration: RegistrationData): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post('registrations/class-info/', {
        registration: registration,
        studioId: studioId,
      })
      .then((result) => {
        RegistrationTracker.trackAddClass({
          studioId,
          registration,
          success: true,
          duration: Date.now() - startTime,
          createAndConfirm: registration.createAndConfirm,
        });
        return result;
      })
      .catch((error) => {
        RegistrationTracker.trackAddClass({
          studioId,
          registration,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
          createAndConfirm: registration.createAndConfirm,
        });
        throw error;
      });
  }

  confirmClass(registration: RegistrationData): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post('registrations/confirm-class-info', {
        registration: registration,
      })
      .then((result) => {
        RegistrationTracker.trackConfirmClass({
          registration,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        RegistrationTracker.trackConfirmClass({
          registration,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  assignCatchUpClass(attendanceId: string, groupId: string, clientId: string): Promise<any> {
    return this.restApiService.put(`registrations/assign-catch-up-class/${attendanceId}/${groupId}/${clientId}`);
  }

  cancelPercent(id: string, percent: number): Promise<any> {
    return this.restApiService.post('registrations/cancel-percent/' + id, {
      percent: percent,
    });
  }

  deleteClientToRegistration(id: string, cancellationReason: string, notifyClient: boolean): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post('registrations/cancel-attendance/' + id, {
        cancellationReason: cancellationReason,
        notifyClient: notifyClient,
      })
      .then((result) => {
        RegistrationTracker.trackDeleteClientFromClass({
          attendanceId: id,
          cancellationReason,
          notifyClient,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        RegistrationTracker.trackDeleteClientFromClass({
          attendanceId: id,
          cancellationReason,
          notifyClient,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  cancelWholeClass(
    studioId: string,
    groupId: string,
    cancellationReason: string,
    notifyClients: boolean
  ): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post(`registrations/cancel-class/${studioId}/${groupId}`, {
        cancellationReason: cancellationReason,
        notifyClients: notifyClients,
      })
      .then((result) => {
        RegistrationTracker.trackCancelWholeClass({
          studioId,
          groupId,
          cancellationReason,
          notifyClients,
          success: true,
          duration: Date.now() - startTime,
          clientsCount: result?.cancelledClientsCount,
        });
        return result;
      })
      .catch((error) => {
        RegistrationTracker.trackCancelWholeClass({
          studioId,
          groupId,
          cancellationReason,
          notifyClients,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  notifyClassesCancellation(studioId: string, groupIds: string[], cancellationReason: string): Promise<any> {
    return this.restApiService.post(`registrations/notify-classes-cancellation/${studioId}/`, {
      groupIds: groupIds,
      cancellationReason: cancellationReason,
    });
  }

  restoreClass(studioId: string, groupId: string): Promise<any> {
    return this.restApiService.post(`registrations/restore-class/${studioId}/${groupId}`);
  }

  setNoShow(id: string, withReimbursement: boolean, cancellationReason: string, notifyClient: boolean): Promise<any> {
    return this.restApiService.put(`registrations/${id}/noShow?withReimbursement=${withReimbursement}`, {
      cancellationReason: cancellationReason,
      notifyClient: notifyClient,
    });
  }

  setPublicNoShow(id: string, data: any): Promise<any> {
    return this.restApiService.put(`registrations/${id}/public-noShow`, data);
  }

  getRegistrationReadyForBilling(
    from: string,
    to: string,
    billingTags?: string[],
    studioClientId?: string,
    studioId?: string,
    teacherIds?: string[],
    confirmedOnly?: boolean,
    includeCalculating?: boolean,
    skip?: number,
    limit?: number,
    sort?: { field: string; direction: string },
    unconfirmedOnly?: boolean,
    search?: string
  ): Promise<any> {
    const queryParams: any = {};

    if (from) queryParams.from = from;
    if (to) queryParams.to = to;
    if (billingTags) queryParams.billingTags = billingTags;
    if (confirmedOnly) queryParams.confirmedOnly = confirmedOnly;
    if (unconfirmedOnly) queryParams.unconfirmedOnly = unconfirmedOnly;
    if (studioClientId) queryParams.studioClientId = studioClientId;
    if (studioId) queryParams.studioId = studioId;
    if (teacherIds) queryParams.teacherIds = teacherIds;
    if (includeCalculating) queryParams.includeCalculating = includeCalculating;
    if (search && search.trim()) queryParams.search = search.trim();

    if (skip !== undefined) queryParams.skip = skip;
    if (limit !== undefined) queryParams.limit = limit;

    if (sort) {
      queryParams.sortField = sort.field;
      queryParams.sortDirection = sort.direction;
    }

    const url = `registrations/billing?${queryString.stringify(queryParams)}`;
    return this.restApiService.get(url);
  }

  getPotentialDebtRegistrations(
    teacherIds?: string[],
    billingTags?: string[],
    studioId?: string,
    search?: string,
    dateOffsets?: number[]
  ): Promise<any> {
    const queryParams: any = {};

    if (teacherIds) queryParams.teacherIds = teacherIds;
    if (billingTags) queryParams.billingTags = billingTags;
    if (studioId) queryParams.studioId = studioId;
    if (search && search.trim()) queryParams.search = search.trim();
    if (dateOffsets) queryParams.dateOffsets = dateOffsets;

    const url = `registrations/potential-debtors?${queryString.stringify(queryParams)}`;
    return this.restApiService.get(url);
  }

  getNotBilledPastAttendance(studioIds?: string[]): Promise<any> {
    let url = `registrations/unbilled-past-attendance`;

    if (studioIds) {
      url += `?${queryString.stringify({ studioIds })}`;
    }
    return this.restApiService.get(url);
  }

  verifyKnownPin(studioId: string, groupId: string, clientToAdd: any): Promise<any> {
    return this.restApiService.post('registrations/validate-with-pin', {
      studioId: studioId,
      groupId: groupId,
      clientToAdd: clientToAdd,
    });
  }

  noPinSignUp(
    studioId: string,
    groupId: string,
    clientToAdd: any,
    payment: any,
    attendancePrice: number
  ): Promise<any> {
    return this.restApiService.post('registrations/no-pin-sign-up', {
      studioId: studioId,
      groupId: groupId,
      clientToAdd: clientToAdd,
      payment: payment,
      paymentAmount: attendancePrice,
    });
  }

  signUpWithOnlinePayment(ownerId: string, studioId: string, onlinePaymentId: string): Promise<any> {
    return this.restApiService.post(
      `registrations/sign-up-with-online-payment/${ownerId}/${studioId}/${onlinePaymentId}`,
      {}
    );
  }

  countAttendanceOnRatePlan(ownerId: string, studioClientId: string, clientRatePlanIds: string[]): Promise<any> {
    return this.restApiService.post(`registrations/ratePlans/${ownerId}/${studioClientId}`, {
      clientRatePlanIds: clientRatePlanIds,
    });
  }

  getFreeSlotsOnCourse(
    studioId: string,
    courseId: string,
    date: any,
    timezone: string,
    allCourseTeachers = false
  ): Promise<any> {
    return this.restApiService.get(
      `registrations/${studioId}/calendar/free-slots/${courseId}?date=${date.format(
        'DD-MM-YYYY'
      )}&timezone=${timezone}&allCourseTeachers=${allCourseTeachers}`
    );
  }

  getAlternativeFreeSlots(
    studioId: string,
    billingTag: string,
    excludeCourseId: string,
    date: any,
    timezone: string,
    allCourseTeachers = false
  ): Promise<any> {
    return this.restApiService.get(
      `registrations/${studioId}/calendar/alternative-free-slots/${billingTag}/${excludeCourseId}?date=${date.format(
        'DD-MM-YYYY'
      )}&timezone=${timezone}&allCourseTeachers=${allCourseTeachers}`
    );
  }

  getFreeSlotOnCourse(ownerId: string, studioId: string, courseId: string, date: any): Promise<any> {
    // never used
    return this.restApiService.get(
      `registrations/${ownerId}/${studioId}/calendar/free-slot/${courseId}?date=${date.format('DD-MM-YYYY-HH-mm')}`
    );
  }

  getFreeSlots(data: {
    ownerId: string;
    studioId: string;
    classDateTime: string;
    timezone: string;
    duration: number;
    teacherId?: string;
    roomId?: string;
  }): Promise<any> {
    const url = `registrations/${data.ownerId}/${data.studioId}/free-slots`;
    return this.restApiService.post(url, {
      classDateTime: data.classDateTime,
      timezone: data.timezone,
      duration: data.duration,
      teacherId: data.teacherId,
      roomId: data.roomId,
    });
  }

  createFreeSlotOnCourse(
    ownerId: string,
    studioId: string,
    courseId: string,
    classInfo: any,
    clientInfo: any
  ): Promise<any> {
    return this.restApiService.post(`registrations/${ownerId}/empty-slot/${studioId}/${courseId}`, {
      classInfo: classInfo,
      clientInfo: clientInfo,
    });
  }

  createFreeSlots(ownerId: string, studioId: string, slots: ClassSlot[], clientInfo: any): Promise<any> {
    return this.restApiService.post(`registrations/${ownerId}/empty-slots/${studioId}`, {
      slots: slots,
      clientInfo: clientInfo,
    });
  }

  registerForCourse(
    studioId: string,
    courseId: string,
    clientInfo: ClientRegistrationInfo,
    courseUrl: string
  ): Promise<any> {
    return this.restApiService.post(`registrations/course/${studioId}/${courseId}`, { clientInfo, courseUrl });
  }

  fillInCourse(ownerId: string, studioId: string, courseId: string, from: string, to: string): Promise<any> {
    return this.restApiService.post(`registrations/${ownerId}/fill-in-course/`, {
      studioId: studioId,
      courseId: courseId,
      from: from,
      to: to,
    });
  }
}

export default RegistrationsApiCall;
